import { Trans } from '@lingui/react/macro';
import { ImageOff, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import * as Three from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useSimulationMesh } from '@/api/hooks';
import { cn } from '@/lib/utils';

export const SimulationMeshViewer = ({
  projectId,
  className,
}: {
  projectId: string;
  className?: string;
}): React.ReactElement => {
  const { data: blob, isLoading, isError } = useSimulationMesh(projectId);
  const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert blob to ArrayBuffer for GLTFLoader.parse()
  useEffect(() => {
    if (!blob) {
      setArrayBuffer(null);
      return;
    }
    let cancelled = false;
    blob.arrayBuffer().then((ab) => {
      if (!cancelled) setArrayBuffer(ab);
    });
    return () => {
      cancelled = true;
    };
  }, [blob]);

  // Three.js scene lifecycle
  useEffect(() => {
    if (!arrayBuffer || !containerRef.current) return;

    const container = containerRef.current;

    // --- Renderer ---
    const renderer = new Three.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight, false);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    container.appendChild(renderer.domElement);

    // --- Scene ---
    const scene = new Three.Scene();
    scene.background = new Three.Color(0xffffff);

    // Soft environment map for reflections
    const whiteScene = new Three.Scene();
    whiteScene.background = new Three.Color(0xffffff);
    const pmremGenerator = new Three.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const envMap = pmremGenerator.fromScene(whiteScene).texture;
    scene.environment = envMap;
    scene.environmentIntensity = 0.5;
    pmremGenerator.dispose();

    // Z-up → Y-up transform (VTK/GLB coordinate system)
    const zUpGroup = new Three.Group();
    zUpGroup.rotation.x = -Math.PI / 2;
    zUpGroup.rotation.z = -Math.PI / 2;
    scene.add(zUpGroup);

    // Axes helper for debugging
    // const axesHelper = new Three.AxesHelper(5);
    // zUpGroup.add(axesHelper);

    // --- Camera ---
    const camera = new Three.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.001, 100);
    camera.position.set(4, 4, 4);
    scene.add(camera);

    // --- Controls ---
    const controls = new TrackballControls(camera, renderer.domElement);
    controls.staticMoving = true;
    controls.rotateSpeed = 2.0;
    controls.target.set(0, 0, 0);
    controls.update();

    // --- Grab cursor behavior ---
    const canvas = renderer.domElement;
    canvas.style.cursor = 'grab';

    const onPointerDown = () => {
      canvas.style.cursor = 'grabbing';
    };
    const onPointerUp = () => {
      canvas.style.cursor = 'grab';
    };
    canvas.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointerup', onPointerUp);

    // --- Lights (VTK LightKit-style fallback) ---
    const topLight = new Three.DirectionalLight(0xffffff, 3.0);
    topLight.position.set(0, 5, 0);
    scene.add(topLight);
    scene.add(topLight.target);

    const bottomLight = new Three.DirectionalLight(0xffffff, 3.0);
    bottomLight.position.set(0, -5, 0);
    bottomLight.target.position.set(0, 0, 0);
    scene.add(bottomLight);
    scene.add(bottomLight.target);

    const sideLight = new Three.DirectionalLight(0xffffff, 0.5);
    sideLight.position.set(5, 2, -2);
    sideLight.target.position.set(0, 0, 0);
    scene.add(sideLight);
    scene.add(sideLight.target);

    const sideLight2 = new Three.DirectionalLight(0xffffff, 0.5);
    sideLight2.position.set(-5, -2, -2);
    sideLight2.target.position.set(0, 0, 0);
    scene.add(sideLight2);
    scene.add(sideLight2.target);

    // --- Load GLB ---
    const loader = new GLTFLoader();
    loader.parse(arrayBuffer, '', (gltf) => {
      const model = gltf.scene;

      // Scale to fit in view
      const box = new Three.Box3().setFromObject(model);
      const size = box.getSize(new Three.Vector3());
      const center = box.getCenter(new Three.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scaleVal = 4 / maxDim;
      model.scale.setScalar(scaleVal);
      model.position.sub(center.multiplyScalar(scaleVal));

      // Fix up materials
      model.traverse((child) => {
        if (!(child as Three.Mesh).isMesh) return;
        const mesh = child as Three.Mesh;
        if (!mesh.material) return;

        if (Array.isArray(mesh.material)) return;

        if (!mesh.geometry.attributes.normal) {
          mesh.geometry.computeVertexNormals();
        }

        if (mesh.name === 'material') {
          const mat = mesh.material as Three.MeshStandardMaterial;
          mat.polygonOffset = true;
          mat.polygonOffsetUnits = 10;
          mat.polygonOffsetFactor = 10;
          mat.opacity = 0.7;
          mat.color = new Three.Color(0x999999);
        }
      });

      zUpGroup.add(model);
    });

    // --- Animation loop ---
    let animationId: number;
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // --- Resize handling ---
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height, false);
      }
    });
    resizeObserver.observe(container);

    // --- Fullscreen resize handling ---
    const handleFullscreenChange = () => {
      requestAnimationFrame(() => {
        if (!container) return;
        const { clientWidth, clientHeight } = container;
        camera.aspect = clientWidth / clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(clientWidth, clientHeight, false);
      });
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // --- Cleanup ---
    return () => {
      cancelAnimationFrame(animationId);
      resizeObserver.disconnect();
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      canvas.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointerup', onPointerUp);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [arrayBuffer]);

  if (isLoading) {
    return (
      <div className="flex h-80 flex-col items-center justify-center gap-3 text-slate-400">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-sm">
          <Trans>Loading…</Trans>
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-80 flex-col items-center justify-center gap-3 text-slate-400">
        <ImageOff size={16} />
        <span className="text-sm">
          <Trans>3D view not available</Trans>
        </span>
      </div>
    );
  }

  if (!arrayBuffer) {
    return (
      <div className="flex h-80 flex-col items-center justify-center gap-3 text-slate-400">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-sm">
          <Trans>Loading…</Trans>
        </span>
      </div>
    );
  }

  return <div ref={containerRef} className={cn('h-[500px] w-full', className)} />;
};
